import { patchRefresh } from '@lib/api/v2/auth';
import { swalReturnWarning } from '@util/commonFunctions';
import { getAllToken, removeToken } from '@util/token';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

type AccessTokenKeyByRefreshToken = Record<RefreshTokenKey, AccessTokenKey>;
type RefreshRequestMap = Partial<Record<RefreshTokenKey, Promise<string>>>;
type RecentRefreshMap = Partial<
  Record<RefreshTokenKey, { expiredAccessToken: string; newAccessToken: string }>
>;
type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _tokenRefreshRetried?: boolean;
};

const accessTokenKeyByRefreshToken: AccessTokenKeyByRefreshToken = {
  adminRefreshToken: 'adminToken',
  merchantRefreshToken: 'merchantToken',
  userRefreshToken: 'userToken',
};
const refreshRequests: RefreshRequestMap = {};
const recentRefreshes: RecentRefreshMap = {};

const getBearerToken = (authorizationHeader?: string): string | null => {
  if (!authorizationHeader) return null;
  return authorizationHeader.split(' ')[1] || null;
};

const clearExpiredSession = () => {
  removeToken();
  window.location.href = '/auth/login';
};

const getRefreshErrorStatus = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) return error.response?.status;
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
};

const isRefreshAuthenticationFailure = (error: unknown): boolean => {
  const status = getRefreshErrorStatus(error);
  return status === 401 || status === 403 || status === 460;
};

export const withRefreshTokenAccepted = function (config: InternalAxiosRequestConfig<any>) {
  const { userRefreshToken, merchantRefreshToken, adminRefreshToken } = getAllToken();
  if (merchantRefreshToken) {
    config.headers['refreshtoken'] = `${merchantRefreshToken}`;
    return config;
  }
  if (userRefreshToken) {
    config.headers['refreshtoken'] = `${userRefreshToken}`;
    return config;
  }
  if (adminRefreshToken) {
    config.headers['refreshtoken'] = `${adminRefreshToken}`;
    return config;
  }
  return config;
};

export const withUserTokenAccepted = function (config: InternalAxiosRequestConfig<any>) {
  const { userToken } = getAllToken();
  // const requestBlocked = localStorage.getItem('requestBlocked');
  // if (requestBlocked === '1') throw new Error();
  if (userToken) config.headers['Authorization'] = `Bearer ${userToken}`;
  return config;
};

export const withAdminTokenAccepted = function (config: InternalAxiosRequestConfig<any>) {
  const { adminToken } = getAllToken();
  // const requestBlocked = localStorage.getItem('requestBlocked');
  // if (requestBlocked === '1') throw new Error();
  if (adminToken) config.headers['Authorization'] = `Bearer ${adminToken}`;
  return config;
};

export const withMerchantTokenAccepted = function (config: InternalAxiosRequestConfig<any>) {
  const { merchantToken } = getAllToken();
  // const requestBlocked = localStorage.getItem('requestBlocked');
  // if (requestBlocked === '1') throw new Error();
  if (merchantToken) config.headers['Authorization'] = `Bearer ${merchantToken}`;
  return config;
};

export const apply = (response: any): Promise<any> => {
  return response;
};

/**
 * 사용자 토큰 에러 시
 * @param error
 * @returns
 */
export const withTokenError = function (error: AxiosError) {
  return Promise.reject(error);
};

/**
 * Authorization 헤더에서 토큰 타입을 감지합니다.
 * @param authorizationHeader - Authorization 헤더 값 (예: "Bearer xxx")
 * @returns 토큰 타입 또는 null
 */
export const detectTokenType = (authorizationHeader?: string): RefreshTokenKey | null => {
  const authorizationToken = getBearerToken(authorizationHeader);
  if (!authorizationToken) return null;

  const tokenList = getAllToken();

  if (tokenList.adminToken && authorizationToken === tokenList.adminToken) {
    return 'adminRefreshToken';
  }
  if (tokenList.merchantToken && authorizationToken === tokenList.merchantToken) {
    return 'merchantRefreshToken';
  }
  if (tokenList.userToken && authorizationToken === tokenList.userToken) {
    return 'userRefreshToken';
  }

  const recentTokenType = (
    Object.entries(recentRefreshes) as [
      RefreshTokenKey,
      RecentRefreshMap[RefreshTokenKey],
    ][]
  ).find(([, refresh]) => refresh?.expiredAccessToken === authorizationToken)?.[0];

  if (recentTokenType) return recentTokenType;

  return null;
};

/**
 * 토큰 갱신을 시도하고 새 액세스 토큰을 반환합니다.
 * @param authorizationHeader - Authorization 헤더 값
 * @returns 새로운 액세스 토큰
 * @throws 토큰 갱신 실패 시 에러
 */
export const attemptTokenRefresh = async (authorizationHeader?: string): Promise<string> => {
  if (!authorizationHeader) {
    clearExpiredSession();
    throw new Error('No authorization header');
  }

  const tokenType = detectTokenType(authorizationHeader);

  if (!tokenType) {
    clearExpiredSession();
    throw new Error('Token type not found');
  }

  const expiredAccessToken = getBearerToken(authorizationHeader);
  const recentRefresh = recentRefreshes[tokenType];
  const currentAccessToken = getAllToken()[accessTokenKeyByRefreshToken[tokenType]];

  if (
    expiredAccessToken &&
    recentRefresh?.expiredAccessToken === expiredAccessToken &&
    recentRefresh.newAccessToken === currentAccessToken
  ) {
    return recentRefresh.newAccessToken;
  }

  if (refreshRequests[tokenType]) return refreshRequests[tokenType];

  const refreshRequest = (async () => {
    try {
      const res = await patchRefresh(tokenType);
      const newAccessToken = res.data.accessToken;

      if (expiredAccessToken) {
        recentRefreshes[tokenType] = {
          expiredAccessToken,
          newAccessToken,
        };
      }

      return newAccessToken;
    } catch (refreshError) {
      if (isRefreshAuthenticationFailure(refreshError)) {
        clearExpiredSession();
      }
      throw refreshError;
    } finally {
      delete refreshRequests[tokenType];
    }
  })();

  refreshRequests[tokenType] = refreshRequest;
  return refreshRequest;
};

export const withTokenRejected = async (error: AxiosError) => {
  const { config, response } = error as AxiosError;

  if (response?.status === 429) {
    swalReturnWarning(
      '현재 접속이 많아 요청을 처리할 수 없습니다.\n잠시 후 다시 시도해 주시면 원활하게 이용하실 수 있습니다.',
    );
    return;
  }

  if (response?.status !== 401 || !config) {
    return Promise.reject(response ?? error);
  }

  const retryConfig = config as RetriableRequestConfig;
  if (retryConfig._tokenRefreshRetried) return Promise.reject(error);

  const authorization = config.headers?.Authorization as string | undefined;
  const newAccessToken = await attemptTokenRefresh(authorization);
  retryConfig._tokenRefreshRetried = true;

  return axios({
    ...retryConfig,
    headers: {
      ...retryConfig.headers,
      Authorization: 'Bearer ' + newAccessToken,
    },
  });
};

/**
 * 토큰이 만료되었을 때 호출되는 함수
 * @param error
 */
export const withTokenExpired = (error: AxiosError) => {
  if (error.response?.status !== 401) return Promise.reject(error);

  removeToken();
  window.location.href = '/auth/login';
  return Promise.reject(error);
};
